/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Input, Component, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmDialogComponent, NotificationService } from '@alfresco/adf-core';
import { PageHeaderComponent, PageStateComponent } from '@alfresco/aca-shared';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AdminService } from '../admin/admin.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PromptDialogComponent } from '../shared/prompt-dialog.component';

/**
 * Groups whose member counts are fetched up front.
 *
 * Each count is a separate request, so the list stays bounded; beyond it the
 * table shows the count only once a group is opened.
 */
const COUNT_LIMIT = 60;

/** Alfresco's authority zone for groups that belong to a Share site. */
const SITE_ZONE = 'APP.SHARE';

interface GroupRow {
  id: string;
  displayName: string;
  isRoot: boolean;
  zones: string[];
  isSiteGroup: boolean;
  /** `null` while unknown — never shown as zero. */
  memberCount: number | null;
}

interface GroupMember {
  id: string;
  displayName: string;
  isGroup: boolean;
}

/**
 * Groups (UI-12).
 *
 * Everything shown is a field the repository returns. Alfresco groups carry no
 * owner, no creation or update timestamp and no enabled flag, so the reference's
 * “เจ้าของกลุ่ม”, “สร้างเมื่อ”, “อัปเดตล่าสุด” and “สถานะ” columns are replaced by
 * attributes that do exist: the authority **zone** (which distinguishes a Share
 * site's groups from directory groups) and `isRoot` (top-level versus nested).
 *
 * Members come from the legacy `api/groups/{shortName}/children` webscript
 * rather than the public members endpoint, which depends on the search index —
 * the same choice the rest of this application already makes.
 */
@Component({
  selector: 'app-groups-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './groups-page.component.html',
  styleUrls: ['./groups-page.component.scss']
})
export class GroupsPageComponent implements OnInit {
  /**
   * Whether the screen is shown inside the settings page rather than on a
   * route of its own — it then drops its own title, because the page it
   * sits in already names it.
   */
  @Input() embedded = false;

  private readonly translate = inject(TranslateService);
  private readonly admin = inject(AdminService);
  private readonly notification = inject(NotificationService);
  private readonly dialog = inject(MatDialog);

  readonly groups = signal<GroupRow[]>([]);
  readonly loading = signal(true);
  readonly failed = signal('');
  readonly selected = signal<GroupRow | null>(null);
  readonly members = signal<GroupMember[]>([]);
  readonly membersLoading = signal(false);
  readonly working = signal(false);

  term = '';
  kind: 'all' | 'site' | 'directory' | 'root' = 'all';

  private readonly termSignal = signal('');
  private readonly kindSignal = signal<'all' | 'site' | 'directory' | 'root'>('all');

  readonly visibleGroups = computed(() => {
    const term = this.termSignal().trim().toLowerCase();
    const kind = this.kindSignal();

    return this.groups().filter((group) => {
      if (kind === 'site' && !group.isSiteGroup) {
        return false;
      }
      if (kind === 'directory' && group.isSiteGroup) {
        return false;
      }
      if (kind === 'root' && !group.isRoot) {
        return false;
      }
      if (!term) {
        return true;
      }
      return group.displayName.toLowerCase().includes(term) || group.id.toLowerCase().includes(term);
    });
  });

  readonly totalCount = computed(() => this.groups().length);
  readonly rootCount = computed(() => this.groups().filter((g) => g.isRoot).length);
  readonly siteCount = computed(() => this.groups().filter((g) => g.isSiteGroup).length);
  readonly nestedCount = computed(() => this.groups().filter((g) => !g.isRoot).length);

  ngOnInit(): void {
    this.reload();
  }

  // ---- data ---------------------------------------------------------------

  reload(): void {
    this.loading.set(true);
    this.failed.set('');

    this.admin.listGroups(0, 500).subscribe({
      next: (res) => {
        const rows: GroupRow[] = (res?.list?.entries ?? []).map((e: any) => {
          const zones: string[] = e.entry.zones ?? [];
          return {
            id: e.entry.id,
            displayName: e.entry.displayName || e.entry.id,
            isRoot: !!e.entry.isRoot,
            zones,
            isSiteGroup: zones.includes(SITE_ZONE),
            memberCount: null
          };
        });

        this.groups.set(rows);
        this.loading.set(false);
        this.loadCounts(rows);

        const current = this.selected();
        if (current) {
          this.selected.set(rows.find((row) => row.id === current.id) ?? null);
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.groups.set([]);
        this.failed.set(this.describe(err, 'DMS.UI.5AAB80'));
      }
    });
  }

  /** Fills in member counts for the first {@link COUNT_LIMIT} groups. */
  private loadCounts(rows: GroupRow[]): void {
    const targets = rows.slice(0, COUNT_LIMIT);
    if (!targets.length) {
      return;
    }

    forkJoin(
      targets.map((row) =>
        this.admin.listGroupChildren(row.id).pipe(
          map((res) => ({ id: row.id, count: this.toMembers(res).length })),
          catchError(() => of({ id: row.id, count: null as number | null }))
        )
      )
    ).subscribe((results) => {
      const byId = new Map(results.map((r) => [r.id, r.count]));
      this.groups.update((list) => list.map((row) => (byId.has(row.id) ? { ...row, memberCount: byId.get(row.id) ?? null } : row)));
    });
  }

  applyFilters(): void {
    this.termSignal.set(this.term);
    this.kindSignal.set(this.kind);
  }

  clearFilters(): void {
    this.term = '';
    this.kind = 'all';
    this.applyFilters();
  }

  // ---- selection ----------------------------------------------------------

  select(group: GroupRow): void {
    if (this.selected()?.id === group.id) {
      this.selected.set(null);
      return;
    }

    this.selected.set(group);
    this.loadMembers(group);
  }

  /** Reads a group's direct members and keeps its row count in step. */
  private loadMembers(group: GroupRow): void {
    this.membersLoading.set(true);
    this.members.set([]);

    this.admin.listGroupChildren(group.id).subscribe({
      next: (res) => {
        const members = this.toMembers(res);
        this.members.set(members);
        this.membersLoading.set(false);
        this.groups.update((list) => list.map((row) => (row.id === group.id ? { ...row, memberCount: members.length } : row)));
      },
      error: () => {
        this.members.set([]);
        this.membersLoading.set(false);
      }
    });
  }

  closePanel(): void {
    this.selected.set(null);
  }

  // ---- actions ------------------------------------------------------------

  createGroup(): void {
    this.dialog
      .open(PromptDialogComponent, {
        data: {
          title: 'DMS.GROUPS_PAGE.NEW_TITLE',
          fields: [
            { key: 'id', label: 'DMS.UI.2C604C', required: true },
            { key: 'displayName', label: 'DMS.UI.B206F6', hint: 'DMS.GROUPS_PAGE.NAME_HINT' }
          ]
        }
      })
      .afterClosed()
      .subscribe((values?: Record<string, string>) => values && this.create(values['id'], values['displayName'] || values['id']));
  }

  private create(name: string, displayName: string): void {
    this.working.set(true);

    this.admin.createGroup({ id: name, displayName }).subscribe({
      next: () => {
        this.working.set(false);
        this.notification.showInfo(this.translate.instant('DMS.UI.D33C37', { p0: displayName }));
        this.reload();
      },
      error: (err) => {
        this.working.set(false);
        this.notification.showError(this.describe(err, 'DMS.UI.E7329A'));
      }
    });
  }

  rename(group: GroupRow): void {
    this.dialog
      .open(PromptDialogComponent, {
        data: { title: 'DMS.UI.B206F6', fields: [{ key: 'displayName', label: 'DMS.UI.B206F6', value: group.displayName, required: true }] }
      })
      .afterClosed()
      .subscribe((values?: Record<string, string>) => {
        const displayName = values?.['displayName'];
        if (displayName && displayName !== group.displayName) {
          this.saveName(group, displayName);
        }
      });
  }

  private saveName(group: GroupRow, displayName: string): void {
    this.admin.updateGroup(group.id, { displayName }).subscribe({
      next: () => {
        this.notification.showInfo(this.translate.instant('DMS.UI.F58DD6'));
        this.reload();
      },
      error: (err) => this.notification.showError(this.describe(err, 'DMS.UI.12F6B0'))
    });
  }

  deleteGroup(group: GroupRow): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'DMS.UI.E3B59A',
          message: this.translate.instant('DMS.UI.414707', { p0: group.displayName }),
          yesLabel: 'DMS.UI.AD7BD3',
          noLabel: 'DMS.UI.7D3094'
        },
        minWidth: '320px',
        restoreFocus: true
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed !== true) {
          return;
        }
        this.admin.deleteGroup(group.id).subscribe({
          next: () => {
            this.notification.showInfo(this.translate.instant('DMS.UI.6C4B90', { p0: group.displayName }));
            this.selected.set(null);
            this.reload();
          },
          error: (err) => this.notification.showError(this.describe(err, 'DMS.UI.7D331A'))
        });
      });
  }

  addMember(group: GroupRow): void {
    this.dialog
      .open(PromptDialogComponent, { data: { title: 'DMS.UI.2BF67C', fields: [{ key: 'id', label: 'DMS.UI.2BF67C', required: true }] } })
      .afterClosed()
      .subscribe((values?: Record<string, string>) => values?.['id'] && this.saveMember(group, values['id']));
  }

  private saveMember(group: GroupRow, id: string): void {
    this.admin.addMember(group.id, id, 'PERSON').subscribe({
      next: () => {
        this.notification.showInfo(this.translate.instant('DMS.UI.9DCC2E', { p0: id }));
        this.loadMembers(group);
      },
      error: (err) => this.notification.showError(this.describe(err, 'DMS.UI.8795FE'))
    });
  }

  removeMember(group: GroupRow, member: GroupMember): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'DMS.UI.BFD577',
          message: this.translate.instant('DMS.UI.4838C0', { p0: member.displayName, p1: group.displayName }),
          yesLabel: 'DMS.UI.6EE8F6',
          noLabel: 'DMS.UI.7D3094'
        },
        minWidth: '320px',
        restoreFocus: true
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed !== true) {
          return;
        }
        this.admin.removeMember(group.id, member.id).subscribe({
          next: () => {
            this.notification.showInfo(this.translate.instant('DMS.UI.1BEA0A', { p0: member.displayName }));
            this.members.update((list) => list.filter((m) => m.id !== member.id));
            this.groups.update((list) =>
              list.map((row) => (row.id === group.id && row.memberCount !== null ? { ...row, memberCount: row.memberCount - 1 } : row))
            );
          },
          error: (err) => this.notification.showError(this.describe(err, 'DMS.UI.C180CE'))
        });
      });
  }

  // ---- view helpers -------------------------------------------------------

  /** The legacy webscript returns either an array or `{ data: [...] }`. */
  private toMembers(res: any): GroupMember[] {
    const raw: any[] = Array.isArray(res) ? res : (res?.data ?? []);
    return raw.map((entry) => {
      const isGroup = (entry.authorityType ?? entry.memberType) === 'GROUP';
      return {
        id: isGroup ? `GROUP_${entry.shortName ?? entry.id}` : (entry.shortName ?? entry.userName ?? entry.id),
        displayName: entry.displayName || entry.fullName || entry.shortName || entry.userName || entry.id,
        isGroup
      };
    });
  }

  kindLabel(group: GroupRow): string {
    return group.isSiteGroup ? 'DMS.UI.403339' : 'DMS.UI.EDBE58';
  }

  levelLabel(group: GroupRow): string {
    return this.translate.instant(group.isRoot ? 'DMS.UI.AA6945' : 'DMS.UI.C6C0C3');
  }

  memberText(group: GroupRow): string {
    return group.memberCount === null ? '—' : `${group.memberCount}`;
  }

  initials(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || (name?.[0] ?? '?').toUpperCase();
  }

  trackById(_: number, group: GroupRow): string {
    return group.id;
  }

  trackByMemberId(_: number, member: GroupMember): string {
    return member.id;
  }

  private describe(error: any, fallback: string): string {
    return error?.error?.error?.briefSummary || error?.message || this.translate.instant(fallback);
  }
}
